import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import axios from 'axios';
import AxiosConfig from '../../../utilities/AxiosConfig';
// @ts-ignore
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
// @ts-ignore
import BootstrapTable from 'react-bootstrap-table-next';
//@ts-ignore
import paginationFactory from 'react-bootstrap-table2-paginator';
import Loader from '../Loader';
import './DoublePanel.css';
import { getDimensions } from '../../../utilities/helper';
import { useDebounce } from '../../../utilities/CustomHooks';
import SplitterLayout from 'react-splitter-layout';
import 'react-splitter-layout/lib/index.css';
import CssAnimate from '../CssAnimate';
// const ColumnToggleList = React.lazy(() => import("./ColumnToggleList"));
import ColumnToggleList from './ColumnToggleList';

interface IProps {
    /** Columns to render in table. Check react-bootstrap-table2 for documentation */
    columns: Array<any>; //? Columns from react-bootstrap-table
    columnsOpen?: Array<number> | Array<any>;
    /** Url to call if you would like the table auto populated. Or empty to set it yourself */
    url: string;
    /** Unique key of each table column. */
    keyField: string;
    /** What to render abover the table display. You recieve handleChange and searchValue as props */
    Header?: (handleChange: any, searchValue: string) => any;
    /** What to render when a row is clicked in the table. You are passed Details, OnClose,  */
    DetailPanel?: any;
    /** Class to put on a row. Recieves a function that is passed row and rowIndex. Returns a string */
    rowClasses?: (row: any, rowIndex: number) => string;
    /** Table props. Refer to react-bootstrap-table2 */
    tableProps?: any;
    /** Whether to allow selection in the table or not. Renders checkboxs on left most column */
    allowSelection?: boolean; //? Enables or disables selection logic 
    /** Custom list of column keys to search in the default searchbox */
    searchList?: Array<{ key: string, customMatch?: (currentSearchTerm: string, value: any) => boolean | null | undefined } | string>;
    delay?: number;
    //? Following props are used if manual is set to true
    /** Setting this will disable the autocall logic */
    manual?: boolean; //? 
    /** Custom Data to feed the table */
    data?: Array<any>;
    /** Show the loader on first load */
    startWithLoader?: boolean;
    /** Custom styles for the wrapping component */
    wrapperStyles?: any;
    /** Toggle the vertical status of the component */
    resizeVertical?: any;
    /** Custom style for the wrapping detail component */
    detailWrapperClassName?: string;
    /** Custom Ref for the table. Grants access to a variety of component features */
    ref?: any;
    wrapperClassName?: string;
    /** Starts the double panel detail as vertical */
    startVertical?: boolean;
    clickCallback?: any;
    /** Controls wether the DoublePanel turns into a modal when opening detail */
    turnIntoModal?: boolean; //? Controls whether detail panel turns to modal on tablet view
    /** Enables or disables column toggle */
    columnToggle?: boolean; //? Enables or disables column toggle
    /** Hides columns with given datafield from rendering */
    hidden?: Array<string>; //? Hides columns with given datafield from rendering
    /** Marks columns that are fixed for toggleable columns */
    unclosableColumns?: Array<string>; //? Marks columns that are fixed for toggleable columns
    /** Indicates where to save column display data in user settings */
    columnToggleTarget?: string; //? Indicates where to save column display data in user settings
    /** Minimum size of secondary split */
    secondaryMinSize?: number; //? Minimum size of secondary split
    /** Width % the detail panel opens at. Defaults to 70. */
    secondaryInitialSize?: number;
    /** Controls wether to paginate the data in the table */
    paginated?: boolean
    /** Refer to react-bootstrap-table2 */
    paginationOptions?: { showTotal: boolean, sizePerPageList: Array<{ text: string, value: number }> } | {}
}

// ? slide-in-right and slide-in-left animations look really good too
const animationLength: number = 600;

const DoublePanel: React.FC<IProps> = forwardRef((props: IProps, ref: any) => {
    const {
        columns,
        url,
        keyField,
        Header,
        DetailPanel,
        rowClasses,
        tableProps,
        allowSelection,
        searchList,
        manual,
        startWithLoader,
        wrapperStyles,
        resizeVertical,
        detailWrapperClassName,
        wrapperClassName,
        clickCallback,
        columnToggle,
        hidden,
        unclosableColumns,
        columnToggleTarget,
        //r-18-lint-dj
        //eslint-disable-next-line
        secondaryMinSize,
        secondaryInitialSize
    } = props;
    const originalData: any = useRef();
    const [data, setData] = useState([] as Array<any>);
    const [details, setDetails] = useState(null as any);
    const [loading, setLoading] = useState(false as boolean);
    //r-18-lint-dj
    //eslint-disable-next-line
    const [error, setError] = useState("" as string);
    const [panelHeight, setPanelHeight] = useState(window.innerHeight - 150 as number);
    const [searchTerm, setSearchTerm] = useState("" as string);
    const [selected, setSelected] = useState([] as Array<any>);
    const [isVertical, setIsVertical] = useState(props.startVertical);
    const [tableRefreshKey, setTableRefreshKey] = React.useState(0);
    const debouncedTerm = useDebounce(searchTerm, props.delay);

    // #region MANUAL SPECIFIC CODE / Initial load code
    //NOTE: https://stackoverflow.com/questions/37949981/call-child-method-from-parent
    //? When in manual mode these are the methods in given ref
    useImperativeHandle(ref, () => ({
        toggleLoading: (isLoading: boolean) => {
            setLoading(isLoading);
            if (isLoading) {
                setDetails(null);
            }
        },
        hideDetails: () => { setDetails(null); }, //? Hides the detail panel
        showDetails: (newDetails: any) => { //? Shows the detail panel with a custom details object
            setDetails(newDetails);
        },
        getDetails: () => details, //? Returns current details
        refreshTable: () => { setTableRefreshKey(tableRefreshKey + 1); }, //? Sets refresh key of table
        triggerFilter: () => handleFilter(), //? Triggers filter
    }));

    React.useEffect(() => {
        if (manual) {
            originalData.current = props.data;
            setData(props.data as Array<any>);
            if (debouncedTerm.length > 0) {
                handleFilter();
            }
        }
        // eslint-disable-next-line 
    }, [props.data, manual])

    useEffect(() => {
        if (!manual) {
            getData();
        } else {
            startWithLoader && setLoading(true);
        }
        // eslint-disable-next-line 
    }, [])

    //#endregion

    const NoDataIndication = () => (
        <div>
            <Loader title="Loading..." />
        </div>
    );

    const rowEvents = {
        onClick: (e: any, row: any, rowIndex: number) => {
            e.preventDefault();
            e.stopPropagation();
            setDetails(row);
            clickCallback && clickCallback(row);
        }
    }

    const selectRow = {
        mode: "checkbox",
        selected: selected.map(item => item[keyField]),
        bgColor: "#C4C5C6",
        onSelect: (row: any, isSelected: boolean) => {
            if (isSelected) {
                setSelected([...selected, row]);
            } else {
                let filtered: Array<any> = [...selected].filter((i: any) => (
                    i[keyField] !== row[keyField]
                ));
                setSelected(filtered);
            }
        },
        onSelectAll: (isSelected: boolean, rows: any) => {
            setSelected(isSelected ? data : []);
        },
        selectionRenderer: ({ mode, ...rest }: any) => {
            const p: any = (({ rowIndex, rowKey, ...r }) => r)(rest);
            return (
                <input className="table-select-checkbox" type={mode} {...p} readOnly />
            )
        }
    }

    const defaultRowClasses = (row: any, rowIndex: number) => {
        let classes = "cursor-on-hover";
        classes += `row-index-${rowIndex}`;
        let copy = details !== null ? { ...details as any } : {};
        if (row[keyField] === copy[keyField]) {
            let key: any = row[keyField]?.[keyField] || "";
            classes += " bg-lightgrey";
            classes += ` row-keyfield-${key}`;
        }
        return classes;
    }

    const getData = () => {
        setLoading(true);
        setDetails(null);
        if (url === "demo") {
            setTimeout(() => {
                setData([
                    { test: "Hello World 1" },
                    { test: "Hello World 2" },
                    { test: "Hello World 3" },
                    { test: "Hello World 4" }
                ]);
                setLoading(false);
            }, 1000)
            return;
        }
        axios.get(process.env.REACT_APP_API_URL + url, AxiosConfig.configValue("min"))
            .then((res: any) => {
                //console.log("Response: ", res);
                originalData.current = res.data;
                setData(res.data);
            })
            .catch((err: any) => {
                //console.log("Error: ", err);
                AxiosConfig.handleError(err);
                if (err.response) { // 5XX or 4XX error
                    setError("There was a problem processing your request.");
                }
                else if (err.request) {
                    // client never received a response, or request never left
                    setError("There was a problem making the request.");
                }
                else if (err.message) {
                    //console.log('Error', err.message);
                    setError(err.message);
                }
            })
            .finally(() => { setLoading(false); })
    }

    const calculateHeight = () => {
        const d = getDimensions(false, {
            vert: [".panel-header", ".top-nav", ...resizeVertical]
        });
        setPanelHeight(d.height);
    }

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        setSearchTerm(e.target.value);
    }

    const handleFilter = () => {
        let search: string = debouncedTerm.toUpperCase();
        let copy: Array<any> = originalData.current ? [...originalData.current] : [];
        if (searchList) {
            copy = copy.filter((item: any) => {
                let isValid: boolean = false;
                searchList.forEach((option: { key: string, customMatch?: any } | string) => {
                    if (typeof option === "string") {
                        //console.log("Searching", item, option, item[option]);
                        if (item[option] !== undefined || item[option] !== null) {
                            isValid = isValid ? true : String(item[option as string])?.toUpperCase().includes(search);
                        }
                    } else {
                        isValid = isValid ? true : option.customMatch ? option.customMatch(search, item[option.key]) : item[option.key].toUpperCase().includes(search);
                    }
                })
                return isValid;
            })
        }
        //console.log("Final Filter: ", copy);
        setData(copy);
    }

    const handleClose = () => {
        setDetails(null);
    }

    const toggleVertical = () => { setIsVertical(!isVertical); }

    useEffect(() => {
        handleFilter();
        // eslint-disable-next-line 
        //r-18-lint-dj
        //eslint-disable-next-line
    }, [debouncedTerm])

    useEffect(() => {
        calculateHeight();
        //r-18-lint-dj
        //eslint-disable-next-line
    }, [data, loading])

    useEffect(() => {
        window.addEventListener('resize', () => {
            calculateHeight()
        })
        //r-18-lint-dj
        //eslint-disable-next-line
    }, [])

    return (
        <div className={wrapperClassName ? wrapperClassName : "col-sm main-content"} id="double-panel" style={wrapperStyles}>
            {Header ?
                <div className="panel-header">
                    <Header
                        loading={loading}
                        handleClose={handleClose}
                        refresh={getData}
                        searchValue={searchTerm}
                        onSearchChange={handleSearchChange}
                        selected={selected}
                        isVertical={isVertical}
                        toggleVertical={toggleVertical}
                        setValue={(newValue: any) => { setSearchTerm(newValue) }}
                    />
                </div>
                : null
            }
            <div style={{ height: panelHeight, overflowY: "hidden" }}>
                <SplitterLayout
                    vertical={isVertical}
                    customClassName={`panels${isVertical ? "" : "-horizontal"}`}
                    percentage={true}
                    primaryMinSize={15}
                    secondaryMinSize={props.secondaryMinSize}
                    secondaryInitialSize={secondaryInitialSize}
                >
                    <div className={`left-panel${(details !== null) ? "-minimized" : ""}`}>
                        <ToolkitProvider
                            keyField={keyField}
                            data={data}
                            columns={hidden !== null ? columns.map((c: any) => ({ ...c, hidden: hidden ? hidden.includes(c.dataField) : false })) : columns}
                            key={tableRefreshKey}
                            columnToggle={columnToggle}
                        >
                            {(p: any) => (
                                loading
                                    ?
                                    <NoDataIndication />
                                    :
                                    <>
                                        {
                                            columnToggle
                                                ? <ColumnToggleList {...p.columnToggleProps} key={JSON.stringify(p.columnToggleProps.toggles)} target={columnToggleTarget} ignore={unclosableColumns} />
                                                : null
                                        }
                                        {props.paginated && <BootstrapTable
                                            id={"double-panel-table" + (allowSelection ? "-with-selection" : "")}
                                            bootstrap4 hover striped
                                            rowEvents={rowEvents}
                                            rowClasses={rowClasses ? rowClasses : defaultRowClasses}
                                            noDataIndication={() => <span>No Data...</span>}
                                            selectRow={allowSelection ? selectRow : undefined}
                                            {...tableProps}
                                            {...p.baseProps}
                                            pagination={paginationFactory(props.paginationOptions)}
                                        />}
                                        {!props.paginated && <BootstrapTable
                                            id={"double-panel-table" + (allowSelection ? "-with-selection" : "")}
                                            bootstrap4 hover striped
                                            rowEvents={rowEvents}
                                            rowClasses={rowClasses ? rowClasses : defaultRowClasses}
                                            noDataIndication={() => <span>No Data...</span>}
                                            selectRow={allowSelection ? selectRow : undefined}
                                            {...tableProps}
                                            {...p.baseProps}
                                        />}
                                    </>
                            )}
                        </ToolkitProvider>
                    </div>
                    {(details !== null && !loading) ?
                        <CssAnimate
                            key={details[keyField]}
                            className={`right-panel${props.turnIntoModal ? "-modal" : ""} ${detailWrapperClassName} ${isVertical ? "overflow-scroll" : ""}`}
                            time={animationLength}
                            style={window.innerWidth > 992 ? wrapperStyles?.height ? { height: wrapperStyles.height } : {} : {}}
                        // animationIn="slide-in-right"
                        >
                            <DetailPanel
                                key={details[keyField]}
                                toggleVertical={toggleVertical}
                                isVertical={isVertical}
                                handleClose={handleClose}
                                refresh={getData}
                                details={details}
                            />
                        </CssAnimate>
                        : null
                    }
                </SplitterLayout>
            </div>
        </div>
    );
})

DoublePanel.defaultProps = {
    tableProps: {},
    url: "demo",
    allowSelection: false,
    manual: false,
    searchList: [],
    delay: 500,
    wrapperStyles: {},
    resizeVertical: [],
    detailWrapperClassName: "",
    wrapperClassName: "",
    startVertical: false,
    turnIntoModal: true,
    columnToggle: false,
    columnToggleTarget: "",
    hidden: [],
    unclosableColumns: [],
    secondaryMinSize: 70,
    secondaryInitialSize: 70,
    paginated: false,
    paginationOptions: {}
}

export default DoublePanel;

export const sortCaret: any = (order: "asc" | "desc", column: any) => <div className="d-flex flex-column m-0 cursor-on-hover">
    <i style={{ marginBottom: -15 }} className={`material-icons ${order === "asc" ? "text-dark" : "text-secondary"}`}>expand_less</i>
    <i className={`material-icons ${order === "desc" ? "text-dark" : "text-secondary"}`}>expand_more</i>
</div>


